import React, { useState } from 'react';
import { OptionFlowAlert } from '../types';
import '../styles/option-flow-visualization.css';

interface OptionFlowVisualizationProps {
  alerts: OptionFlowAlert[];
  ticker?: string;
  selectedDate?: string;
  onDateChange?: (date: string) => void;
  availableDates?: string[]; // Array of available dates in YYYY-MM-DD format
}

export const OptionFlowVisualization: React.FC<OptionFlowVisualizationProps> = ({ alerts, ticker, selectedDate, onDateChange, availableDates = [] }) => {
  // Use provided date or get latest date from alerts
  const [internalDate, setInternalDate] = useState<string>(() => {
    if (alerts.length > 0) {
      const dates = alerts.map(alert => new Date(alert.created_at).toISOString().split('T')[0]);
      return dates.sort().reverse()[0];
    }
    return '';
  });
  
  // Use controlled date if provided, otherwise use internal state
  const currentDate = selectedDate !== undefined ? selectedDate : internalDate;
  const handleDateChange = (date: string) => {
    if (onDateChange) {
      onDateChange(date);
    } else {
      setInternalDate(date);
    }
  };

  // Calculate min and max dates from availableDates
  const minDate = availableDates.length > 0 ? availableDates.sort()[0] : '';
  const maxDate = availableDates.length > 0 ? availableDates.sort().reverse()[0] : new Date().toISOString().split('T')[0];

  // Use alerts directly (filtering is done by parent component)
  const dateFilteredAlerts = alerts;

  // If no alerts, show visualization with date picker but no data
  if (dateFilteredAlerts.length === 0) {
    return (
      <div className="option-flow-visualization">
        <div className="visualization-header">
          <h3 className="visualization-title">
            {ticker ? `${ticker} Flow Analysis` : 'Flow Analysis'}
          </h3>
          <div className="date-picker-container">
            <label htmlFor="flow-date-picker" className="date-picker-label">
              📅 Date:
            </label>
            <input
              id="flow-date-picker"
              type="date"
              value={currentDate}
              onChange={(e) => handleDateChange(e.target.value)}
              className="date-picker-input"
              min={minDate}
              max={maxDate}
            />
            {currentDate && (
              <button
                onClick={() => handleDateChange('')}
                className="date-clear-button"
                title="Show all dates"
              >
                ✕
              </button>
            )}
          </div>
        </div>
        <div className="no-data-message">
          <div className="no-data-icon">📅</div>
          <p>No option flow data available{currentDate ? ` for ${new Date(currentDate + 'T12:00:00').toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
          })}` : ''}</p>
          <p className="no-data-hint">Try selecting a different date or clear the filter</p>
        </div>
      </div>
    );
  }

  // Calculate aggregate statistics from date-filtered alerts
  const callAlerts = dateFilteredAlerts.filter(a => a.type === 'call');
  const putAlerts = dateFilteredAlerts.filter(a => a.type === 'put');

  const calculateBidSidePremium = (alert: OptionFlowAlert) => {
    return typeof alert.total_bid_side_prem === 'string'
      ? parseFloat(alert.total_bid_side_prem)
      : alert.total_bid_side_prem;
  };

  const calculateAskSidePremium = (alert: OptionFlowAlert) => {
    return typeof alert.total_ask_side_prem === 'string'
      ? parseFloat(alert.total_ask_side_prem)
      : alert.total_ask_side_prem;
  };

  // Detailed breakdown: Buy vs Sell for Calls and Puts
  // Ask side = BUYING (aggressive, at ask price)
  // Bid side = SELLING (passive, at bid price)
  
  const callBuyPremium = callAlerts.reduce((sum, alert) => sum + calculateAskSidePremium(alert), 0);
  const callSellPremium = callAlerts.reduce((sum, alert) => sum + calculateBidSidePremium(alert), 0);
  const putBuyPremium = putAlerts.reduce((sum, alert) => sum + calculateAskSidePremium(alert), 0);
  const putSellPremium = putAlerts.reduce((sum, alert) => sum + calculateBidSidePremium(alert), 0);

  // Totals
  const totalCallPremium = callBuyPremium + callSellPremium;
  const totalPutPremium = putBuyPremium + putSellPremium;
  const totalPremium = totalCallPremium + totalPutPremium;

  const totalCallVolume = callAlerts.reduce((sum, alert) => sum + (alert.volume || 0), 0);
  const totalPutVolume = putAlerts.reduce((sum, alert) => sum + (alert.volume || 0), 0);
  const totalVolume = totalCallVolume + totalPutVolume;

  // Call and Put percentages
  const callPercentage = totalPremium > 0 ? (totalCallPremium / totalPremium) * 100 : 0;
  const putPercentage = totalPremium > 0 ? (totalPutPremium / totalPremium) * 100 : 0;

  // Advanced sentiment based on buy/sell breakdown
  // Call buying & Put selling = Bullish
  // Put buying & Call selling = Bearish
  const bullishPremium = callBuyPremium + putSellPremium;
  const bearishPremium = putBuyPremium + callSellPremium;
  
  const isBullish = bullishPremium > bearishPremium;
  const sentiment = isBullish ? 'Bullish' : 'Bearish';
  const sentimentPercentage = totalPremium > 0 
    ? Math.max(bullishPremium, bearishPremium) / totalPremium * 100 
    : 0;

  // Buy vs Sell percentages within each type
  const callBuyPercentage = totalCallPremium > 0 ? (callBuyPremium / totalCallPremium) * 100 : 0;
  const callSellPercentage = totalCallPremium > 0 ? (callSellPremium / totalCallPremium) * 100 : 0;
  const putBuyPercentage = totalPutPremium > 0 ? (putBuyPremium / totalPutPremium) * 100 : 0;
  const putSellPercentage = totalPutPremium > 0 ? (putSellPremium / totalPutPremium) * 100 : 0;

  // Format currency
  const formatCurrency = (value: number): string => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return `$${value.toFixed(0)}`;
  };

  // Format the selected date for display
  const formattedDate = currentDate 
    ? new Date(currentDate + 'T12:00:00').toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    : 'All dates';

  return (
    <div className="option-flow-visualization">
      <div className="visualization-header">
        <div className="visualization-title-section">
          <h3 className="visualization-title">
            {ticker ? `${ticker} Flow Analysis` : 'Flow Analysis'}
          </h3>
          <span className="visualization-subtitle">
            {dateFilteredAlerts.length} alerts • Total Volume: {totalVolume.toLocaleString()} • {formattedDate}
          </span>
        </div>
        <div className="date-picker-container">
          <label htmlFor="flow-date-picker" className="date-picker-label">
            📅 Date:
          </label>
          <input
            id="flow-date-picker"
            type="date"
            value={currentDate}
            onChange={(e) => handleDateChange(e.target.value)}
            className="date-picker-input"
            min={minDate}
            max={maxDate}
          />
          {currentDate && (
            <button
              onClick={() => handleDateChange('')}
              className="date-clear-button"
              title="Show all dates"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="visualization-cards">
        {/* Flow Sentiment Card */}
        <div className="viz-card sentiment-card">
          <div className="viz-card-header">
            <span className="viz-card-label">Flow sentiment</span>
          </div>
          <div className="viz-card-content">
            <div className={`sentiment-value ${isBullish ? 'bullish' : 'bearish'}`}>
              {sentiment}
            </div>
            <div className="sentiment-bar-container">
              <div 
                className={`sentiment-bar ${isBullish ? 'bullish' : 'bearish'}`}
                style={{ width: `${sentimentPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Total Premium Card */}
        <div className="viz-card total-premium-card">
          <div className="viz-card-header">
            <span className="viz-card-label">Total Premium</span>
          </div>
          <div className="viz-card-content">
            <div className="total-premium-value">
              {formatCurrency(totalPremium)}
            </div>
            <div className="total-premium-breakdown">
              <div className="premium-breakdown-item">
                <span className="breakdown-label">Calls</span>
                <span className="breakdown-value call-value">{formatCurrency(totalCallPremium)}</span>
              </div>
              <div className="premium-breakdown-item">
                <span className="breakdown-label">Puts</span>
                <span className="breakdown-value put-value">{formatCurrency(totalPutPremium)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Net Flow Direction Card */}
        <div className="viz-card flow-direction-card">
          <div className="viz-card-header">
            <span className="viz-card-label">Net Flow</span>
            <span className={`viz-card-amount ${isBullish ? 'bullish-text' : 'bearish-text'}`}>
              {formatCurrency(Math.abs(bullishPremium - bearishPremium))}
            </span>
          </div>
          <div className="viz-card-content">
            <div className="flow-direction-bars">
              <div className="flow-direction-item">
                <span className="flow-direction-label">Bullish</span>
                <div className="flow-direction-bar-container">
                  <div 
                    className="flow-direction-bar bullish-bar"
                    style={{ 
                      width: totalPremium > 0 ? `${(bullishPremium / totalPremium) * 100}%` : '0%' 
                    }}
                  ></div>
                </div>
                <span className="flow-direction-value">{formatCurrency(bullishPremium)}</span>
              </div>
              <div className="flow-direction-item">
                <span className="flow-direction-label">Bearish</span>
                <div className="flow-direction-bar-container">
                  <div 
                    className="flow-direction-bar bearish-bar"
                    style={{ 
                      width: totalPremium > 0 ? `${(bearishPremium / totalPremium) * 100}%` : '0%' 
                    }}
                  ></div>
                </div>
                <span className="flow-direction-value">{formatCurrency(bearishPremium)}</span>
              </div>
            </div>
            <div className="net-flow-indicator">
              <span className="net-flow-label">Net:</span>
              <span className={`net-flow-value ${isBullish ? 'bullish-text' : 'bearish-text'}`}>
                {isBullish ? '+' : '-'}{formatCurrency(Math.abs(bullishPremium - bearishPremium))}
              </span>
            </div>
          </div>
        </div>

        {/* Call Flow Card */}
        <div className="viz-card flow-card call-flow-card">
          <div className="viz-card-header">
            <span className="viz-card-label">Call flow</span>
            <span className="viz-card-amount">{formatCurrency(totalCallPremium)}</span>
          </div>
          <div className="viz-card-content">
            <div className="circular-progress-container">
              <svg className="circular-progress" viewBox="0 0 120 120">
                <circle
                  className="circular-progress-bg"
                  cx="60"
                  cy="60"
                  r="50"
                  fill="none"
                  stroke="#2a2d3a"
                  strokeWidth="10"
                />
                <circle
                  className="circular-progress-bar call-progress"
                  cx="60"
                  cy="60"
                  r="50"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={`${callPercentage * 3.14} 314`}
                  transform="rotate(-90 60 60)"
                />
              </svg>
              <div className="circular-progress-text">
                <span className="progress-percentage">{callPercentage.toFixed(1)}%</span>
              </div>
            </div>
            <div className="flow-stats">
              <div className="flow-stat">
                <span className="flow-stat-label">Buy (Ask)</span>
                <span className="flow-stat-value buy-side">{formatCurrency(callBuyPremium)}</span>
                <span className="flow-stat-percent">{callBuyPercentage.toFixed(1)}%</span>
              </div>
              <div className="flow-stat">
                <span className="flow-stat-label">Sell (Bid)</span>
                <span className="flow-stat-value sell-side">{formatCurrency(callSellPremium)}</span>
                <span className="flow-stat-percent">{callSellPercentage.toFixed(1)}%</span>
              </div>
            </div>
            <div className="flow-volume-info">
              Vol: {totalCallVolume.toLocaleString()} • Contracts: {callAlerts.length}
            </div>
          </div>
        </div>

        {/* Put Flow Card */}
        <div className="viz-card flow-card put-flow-card">
          <div className="viz-card-header">
            <span className="viz-card-label">Put flow</span>
            <span className="viz-card-amount">{formatCurrency(totalPutPremium)}</span>
          </div>
          <div className="viz-card-content">
            <div className="circular-progress-container">
              <svg className="circular-progress" viewBox="0 0 120 120">
                <circle
                  className="circular-progress-bg"
                  cx="60"
                  cy="60"
                  r="50"
                  fill="none"
                  stroke="#2a2d3a"
                  strokeWidth="10"
                />
                <circle
                  className="circular-progress-bar put-progress"
                  cx="60"
                  cy="60"
                  r="50"
                  fill="none"
                  stroke="#ef4444"
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={`${putPercentage * 3.14} 314`}
                  transform="rotate(-90 60 60)"
                />
              </svg>
              <div className="circular-progress-text">
                <span className="progress-percentage">{putPercentage.toFixed(1)}%</span>
              </div>
            </div>
            <div className="flow-stats">
              <div className="flow-stat">
                <span className="flow-stat-label">Buy (Ask)</span>
                <span className="flow-stat-value buy-side">{formatCurrency(putBuyPremium)}</span>
                <span className="flow-stat-percent">{putBuyPercentage.toFixed(1)}%</span>
              </div>
              <div className="flow-stat">
                <span className="flow-stat-label">Sell (Bid)</span>
                <span className="flow-stat-value sell-side">{formatCurrency(putSellPremium)}</span>
                <span className="flow-stat-percent">{putSellPercentage.toFixed(1)}%</span>
              </div>
            </div>
            <div className="flow-volume-info">
              Vol: {totalPutVolume.toLocaleString()} • Contracts: {putAlerts.length}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

